import cn from 'classnames';
import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { _isNil, _isNotBlank, _notNil } from '@/littledash';

import './Menu.scss';

type MenuKey = string;

type OpenContextValue = [Array<MenuKey>, Dispatch<SetStateAction<Array<MenuKey>>>];
type SelectedContextValue = [Array<MenuKey | undefined>, Dispatch<SetStateAction<Array<MenuKey | undefined>>>];

const OpenContext = createContext<OpenContextValue | undefined>(undefined);
const SelectedContext = createContext<SelectedContextValue | undefined>(undefined);
const CollapsedContext = createContext<boolean>(false);

const useOpenContext = (): OpenContextValue => {
  const context = useContext(OpenContext);
  if (_isNil(context)) {
    throw new Error('Menu compound components must be rendered within a <Menu>');
  }
  return context;
};

const useSelectedContext = (): SelectedContextValue => {
  const context = useContext(SelectedContext);
  if (_isNil(context)) {
    throw new Error('Menu compound components must be rendered within a <Menu>');
  }
  return context;
};

interface MenuProps {
  defaultOpen?: Array<MenuKey>;
  defaultSelected?: Array<MenuKey | undefined>;
  isCollapsed?: boolean;
  children?: ReactNode;
}

/**
 * Root of the navigation menu compound component. Provides the open (expanded
 * sub-menus), selected (active item) and collapsed state to its descendant
 * `MenuItem` and `SubMenu` components via context.
 */
export const Menu: FC<MenuProps> = ({ defaultOpen = [], defaultSelected = [], isCollapsed = false, children }) => {
  const [open, setOpen] = useState<Array<MenuKey>>(defaultOpen);
  const [selected, setSelected] = useState<Array<MenuKey | undefined>>(defaultSelected);

  useEffect(() => {
    setSelected((previous) =>
      previous.length === defaultSelected.length && previous.every((value, index) => value === defaultSelected[index])
        ? previous
        : defaultSelected
    );
  }, [defaultSelected, setSelected]);

  const openValue = useMemo<OpenContextValue>(() => [open, setOpen], [open]);
  const selectedValue = useMemo<SelectedContextValue>(() => [selected, setSelected], [selected]);

  return (
    <div
      className={cn('menu', { '--is-collapsed': isCollapsed })}
      data-test-component="Menu"
      data-test-element="container"
    >
      <CollapsedContext.Provider value={isCollapsed}>
        <SelectedContext.Provider value={selectedValue}>
          <OpenContext.Provider value={openValue}>{children}</OpenContext.Provider>
        </SelectedContext.Provider>
      </CollapsedContext.Provider>
    </div>
  );
};

interface MenuItemProps {
  index: MenuKey;
  icon?: ReactNode;
  onClick?: () => void;
  children?: ReactNode;
  disabled?: boolean;
}

/**
 * A selectable leaf item within a `Menu`. Selecting it marks the item as active
 * and invokes the optional `onClick` handler.
 */
export const MenuItem: FC<MenuItemProps> = ({ index, icon, onClick, children, disabled = false }) => {
  const isCollapsed = useContext(CollapsedContext);
  const [selected, setSelected] = useSelectedContext();
  const isActive = selected.includes(index);

  const handleClick = () => {
    setSelected([index]);
    onClick?.();
  };

  return (
    <div
      data-tooltip-id="global-tooltip-id"
      data-tooltip-content={isCollapsed && typeof children === 'string' ? children : ''}
      onClick={handleClick}
      className={cn('menu-item', { ui__disabled: disabled, '--is-active': isActive })}
      data-test-component="MenuItem"
      data-test-element="item"
      data-test-key={index}
    >
      {_notNil(icon) && (
        <div className="menu-icon" data-test-element="menu-icon">
          {icon}
        </div>
      )}
      <div className="menu-title noselect" data-test-element="menu-title">
        {children}
      </div>
    </div>
  );
};

interface SubMenuProps {
  index: MenuKey;
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  disabled?: boolean;
  /** The current route pathname, used to auto-open the sub-menu on a descendant route. */
  url: string;
  /** Resolved route paths of this sub-menu's children, used to detect descendant routes. */
  childRoutes: Array<string>;
}

/**
 * A collapsible group of `MenuItem`s within a `Menu`. Its open/closed state is
 * toggled on click and forced open whenever the current route (`url`) belongs to
 * one of its children — an exact match or a nested descendant of a child route.
 */
export const SubMenu: FC<SubMenuProps> = ({ index, icon, title, children, disabled = false, url, childRoutes }) => {
  const [open, setOpen] = useOpenContext();

  const handleClick = () => {
    if (open.includes(index)) {
      setOpen((current) => current.filter((item) => item !== index));
    } else {
      setOpen((current) => [...current, index]);
    }
  };

  const isOnDescendantRoute = childRoutes.some(
    (childRoute) => _isNotBlank(childRoute) && (url === childRoute || url.startsWith(`${childRoute}/`))
  );
  const isOpen = open.includes(index) || isOnDescendantRoute;

  return (
    <div
      className={cn('menu-sub-menu', { ui__disabled: disabled })}
      data-test-component="SubMenu"
      data-test-element="submenu"
    >
      <div
        onClick={handleClick}
        className={cn('menu-item', 'menu-sub-menu--title', { '--is-open': isOpen })}
        data-test-element="submenu-title"
      >
        <div className="flex justify-between items-center">
          {_notNil(icon) && (
            <div className="menu-icon" data-test-element="menu-icon">
              {icon}
            </div>
          )}
          <div className={cn('menu-title', 'noselect', { ml3: _notNil(icon) })} data-test-element="menu-title">
            {title}
          </div>
        </div>
        <i className="menu-sub-menu--sub-menu-indicator" />
      </div>
      <div className={cn('menu-sub-menu--menu', { '--is-open': isOpen })} data-test-element="submenu-menu">
        {children}
      </div>
    </div>
  );
};
